import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { apiFetch } from "./api";

const EMPTY = { title: "", slug: "", excerpt: "", body: "", published: true };

const inputCls =
    "mt-1.5 w-full rounded-lg border border-white/15 bg-[#121722] px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-sky-400";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-slate-500";

export const Blog = ({ token, onAuthError }) => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null = list, {} = editor
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setPosts(await apiFetch("/admin/blog", { token }));
        } catch (err) {
            if (err.status === 401) return onAuthError();
            toast.error("Could not load posts", { description: err.message });
        } finally {
            setLoading(false);
        }
    }, [token, onAuthError]);

    useEffect(() => {
        load();
    }, [load]);

    const save = async () => {
        if (!editing.title.trim()) {
            toast.error("Title is required");
            return;
        }
        setSaving(true);
        try {
            if (editing.id) {
                await apiFetch(`/blog/${editing.id}`, {
                    token,
                    method: "PUT",
                    body: editing,
                });
                toast.success("Article updated");
            } else {
                await apiFetch("/blog", { token, method: "POST", body: editing });
                toast.success("Article published", {
                    description: "It's live at /blog and in your sitemap.",
                });
            }
            setEditing(null);
            load();
        } catch (err) {
            if (err.status === 401) return onAuthError();
            toast.error("Save failed", { description: err.message });
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        try {
            await apiFetch(`/blog/${id}`, { token, method: "DELETE" });
            setPosts((ps) => ps.filter((p) => p.id !== id));
            toast.success("Article deleted");
        } catch (err) {
            if (err.status === 401) return onAuthError();
            toast.error("Delete failed", { description: err.message });
        }
    };

    if (editing) {
        return (
            <div data-testid="blog-editor">
                <div className="flex items-center justify-between">
                    <h3 className="font-display text-lg font-semibold text-white">
                        {editing.id ? "Edit article" : "New article"}
                    </h3>
                    <button
                        data-testid="blog-editor-cancel"
                        onClick={() => setEditing(null)}
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white hover:border-white/40"
                    >
                        <X className="h-3.5 w-3.5" /> Cancel
                    </button>
                </div>
                <div className="mt-6 flex flex-col gap-5 rounded-2xl border border-white/10 bg-[#0C1019] p-6">
                    <div>
                        <label className={labelCls}>Title</label>
                        <input
                            data-testid="blog-field-title"
                            value={editing.title}
                            onChange={(e) =>
                                setEditing((p) => ({ ...p, title: e.target.value }))
                            }
                            placeholder="How Much Does an Extended Car Warranty Cost?"
                            className={inputCls}
                        />
                    </div>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <div>
                            <label className={labelCls}>URL slug (optional)</label>
                            <input
                                data-testid="blog-field-slug"
                                value={editing.slug}
                                onChange={(e) =>
                                    setEditing((p) => ({ ...p, slug: e.target.value }))
                                }
                                placeholder="auto-generated from title"
                                className={inputCls}
                            />
                        </div>
                        <div className="flex items-end pb-1">
                            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-300">
                                <input
                                    data-testid="blog-field-published"
                                    type="checkbox"
                                    checked={editing.published}
                                    onChange={(e) =>
                                        setEditing((p) => ({
                                            ...p,
                                            published: e.target.checked,
                                        }))
                                    }
                                    className="h-4 w-4 accent-sky-400"
                                />
                                Published (visible on /blog)
                            </label>
                        </div>
                    </div>
                    <div>
                        <label className={labelCls}>Excerpt (shown on cards &amp; Google)</label>
                        <textarea
                            data-testid="blog-field-excerpt"
                            rows={2}
                            value={editing.excerpt}
                            onChange={(e) =>
                                setEditing((p) => ({ ...p, excerpt: e.target.value }))
                            }
                            className={inputCls}
                        />
                    </div>
                    <div>
                        <label className={labelCls}>Body</label>
                        <p className="mt-1 text-[11px] text-slate-600">
                            Blank line = new paragraph. Start a line with ## for a
                            heading, or - for bullet lists.
                        </p>
                        <textarea
                            data-testid="blog-field-body"
                            rows={14}
                            value={editing.body}
                            onChange={(e) =>
                                setEditing((p) => ({ ...p, body: e.target.value }))
                            }
                            className={`${inputCls} font-mono text-[13px] leading-relaxed`}
                        />
                    </div>
                    <button
                        data-testid="blog-save-btn"
                        onClick={save}
                        disabled={saving}
                        className="inline-flex w-fit items-center gap-2 rounded-full bg-sky-400 px-7 py-3 text-sm font-bold text-[#0A0D14] transition-colors hover:bg-sky-300 disabled:opacity-60"
                    >
                        {saving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="h-4 w-4" />
                        )}
                        {saving ? "Saving…" : editing.id ? "Save Changes" : "Publish Article"}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-blog-tab">
            <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400">
                    {posts.length} {posts.length === 1 ? "article" : "articles"} —
                    published posts appear at /blog and in your sitemap automatically.
                </p>
                <button
                    data-testid="blog-new-btn"
                    onClick={() => setEditing({ ...EMPTY })}
                    className="inline-flex items-center gap-2 rounded-full bg-sky-400 px-5 py-2.5 text-xs font-bold text-[#0A0D14] transition-colors hover:bg-sky-300"
                >
                    <Plus className="h-3.5 w-3.5" /> New Article
                </button>
            </div>
            <div className="mt-5 flex flex-col gap-3">
                {loading ? (
                    <p className="py-10 text-center text-sm text-slate-500">Loading…</p>
                ) : posts.length === 0 ? (
                    <p className="rounded-2xl border border-white/10 bg-[#0C1019] py-10 text-center text-sm text-slate-500">
                        No articles yet. Publish your first one.
                    </p>
                ) : (
                    posts.map((p) => (
                        <div
                            key={p.id}
                            data-testid={`blog-row-${p.id}`}
                            className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-[#0C1019] p-5"
                        >
                            <div className="min-w-0">
                                <p className="truncate font-display text-base font-semibold text-white">
                                    {p.title}
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                    /blog/{p.slug} ·{" "}
                                    {new Date(p.created_at).toLocaleDateString()} ·{" "}
                                    <span
                                        className={
                                            p.published
                                                ? "text-sky-400"
                                                : "text-slate-500"
                                        }
                                    >
                                        {p.published ? "Published" : "Draft"}
                                    </span>
                                </p>
                            </div>
                            <div className="flex shrink-0 gap-2">
                                <button
                                    data-testid={`blog-edit-${p.id}`}
                                    onClick={() => setEditing({ ...EMPTY, ...p })}
                                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white hover:border-sky-400/50"
                                >
                                    <Pencil className="h-3.5 w-3.5" /> Edit
                                </button>
                                <button
                                    data-testid={`blog-delete-${p.id}`}
                                    onClick={() => remove(p.id)}
                                    className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-300"
                                    aria-label="Delete article"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
